import React from 'react';

const retailItems = [
  { title: 'Available in 100g, 200g, 250g', detail: 'Choose your package size' },
  { title: 'Freshly medium roasted', detail: 'Roasted in small lots, shipped the same week' },
  { title: 'Whole beans or ground', detail: 'Ground to suit your brewing method' },
  { title: 'Screen 12, 15 & 18', detail: 'Three graded sizes to match your brew' },
];

const bulkItems = [
  { title: 'Custom quantities', detail: 'From small batches to container loads' },
  { title: 'Green or roasted', detail: 'Buy green to roast yourself, or roasted and ready' },
  { title: 'Custom grind', detail: 'Ground to your exact specification' },
  { title: 'Direct from the farm', detail: 'No middlemen, full traceability' },
  { title: 'Long-term supply', detail: 'Standing agreements for cafés and chains' },
];

const extras = [
  { title: 'Free samples', detail: 'Try the coffee before you commit to an order.' },
  { title: 'Regional delivery', detail: 'Shipping across Pakistan and East Africa.' },
  { title: 'Custom roasting', detail: 'Bulk orders can request their own roast profile.' },
];

function ItemList({ items, tone }) {
  const dark = tone === 'dark';
  return (
    <ul className={`divide-y ${dark ? 'divide-gray-800 border-gray-800' : 'divide-gray-200 border-gray-200'} border-t`}>
      {items.map((item) => (
        <li key={item.title} className="py-3.5">
          <p className={`font-semibold ${dark ? 'text-white' : 'text-gray-900'}`}>{item.title}</p>
          <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-600'}`}>{item.detail}</p>
        </li>
      ))}
    </ul>
  );
}

export default function HowToOrder() {
  return (
    <section id="howtoorder" className="py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">

          <div className="mb-12 lg:mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-clay mb-4">How to Order</p>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-gray-900 mb-6">
              Retail bags or bulk sacks
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl leading-relaxed">
              Home brewers order roasted bags. Cafés, roasters and distributors buy green or roasted by the kilo, up to full containers.
            </p>
          </div>

          {/* Asymmetric: 5/12 retail, 7/12 wholesale */}
          <div className="grid grid-cols-1 md:grid-cols-12 md:items-start gap-6 lg:gap-8 mb-16">
            <div className="md:col-span-5 bg-white rounded-3xl p-6 lg:p-8 border border-gray-200">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 mb-3">For home brewers</p>
              <h3 className="font-heading text-3xl text-gray-900 mb-6">Retail packages</h3>
              <ItemList items={retailItems} />
            </div>

            <div className="md:col-span-7 bg-gray-900 rounded-3xl p-6 lg:p-10 border border-gold-500/60 flex flex-col">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-400 mb-3">Best value, duty paid</p>
              <h3 className="font-heading text-3xl text-white mb-6">Bulk orders</h3>
              <ItemList items={bulkItems} tone="dark" />
              <a
                href="/wholesale"
                className="mt-8 inline-flex items-center justify-center gap-2 w-full sm:w-auto sm:self-start bg-gold-500 hover:bg-gold-400 text-gray-900 font-bold text-sm sm:text-base px-6 sm:px-8 py-3.5 rounded-full transition-[background-color,transform] duration-200 active:scale-[0.97]"
              >
                See the wholesale page
                <span aria-hidden="true">&rarr;</span>
              </a>
            </div>
          </div>

          <dl className="grid grid-cols-1 md:grid-cols-3 gap-y-6 md:gap-x-10 border-t border-gray-300 pt-10 mb-12">
            {extras.map((extra) => (
              <div key={extra.title}>
                <dt className="font-heading text-2xl text-clay mb-2">{extra.title}</dt>
                <dd className="text-gray-700">{extra.detail}</dd>
              </div>
            ))}
          </dl>

          <a
            href="https://wa.me/447716089293"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 bg-gold-500 hover:bg-gold-400 text-gray-900 font-bold px-10 py-4 rounded-full transition-[background-color,transform] duration-200 active:scale-[0.97]"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Start Your Order
          </a>

        </div>
      </div>
    </section>
  );
}
